import type { ReactElement } from 'react'
import Tooltip from '@mui/material/Tooltip'
import { useTranslation } from 'react-i18next'
import { useRights } from '@/hooks/use-rights'
import useTranslate from '@/hooks/use-translate'
import type { RenderState } from '@/lib/authz/resolve'
import type { RightTarget } from '@/lib/authz/rights'

/**
 * Ce que les accès du membre font d'une commande (décision 16), et la phrase qui l'explique quand elle est
 * refusée : le droit qui manque, à qui le demander. Hors mode actif, toujours `enabled`.
 */
export const useRightCheck = (
  action: string,
  target?: RightTarget
): { state: RenderState; reason: string | null } => {
  const { t } = useTranslate('settings')
  const { i18n } = useTranslation()
  const rights = useRights()
  const { state, grantors } = rights.check(action, target)
  if (state === 'enabled') return { state, reason: null }

  // Le libellé du catalogue chargé ; le code de l'action avant, ou s'il ne la nomme pas.
  const missing = t('authzRightMissing', { action: rights.label(action) })
  const ask =
    grantors.length > 0
      ? t('authzRightAskPeople', {
          names: new Intl.ListFormat(i18n?.language || 'fr', {
            type: 'disjunction',
          }).format(grantors.map((grantor) => grantor.name)),
        })
      : t('authzRightAskAdmin')
  return { state, reason: `${missing} ${ask}` }
}

interface RightGateProps {
  /** L'action du catalogue que la commande déclenche. */
  action: string
  /** L'agence de la ressource ; plusieurs : il faut le droit sur chacune ; aucune : quelque part. */
  target?: RightTarget
  /** La commande ; `disabled` vaut vrai quand les accès du membre la grisent. */
  children: (disabled: boolean) => ReactElement
}

/**
 * Une commande selon les droits du membre (TEC-1677, décision 16) : telle quelle s'ils la permettent, grisée
 * avec une infobulle (le droit qui manque, à qui le demander) pour agir ou exécuter, absente pour supprimer ou
 * gérer les droits. Hors mode actif, toujours telle quelle : l'écran garde sa règle d'avant.
 */
const RightGate = ({ action, target, children }: RightGateProps) => {
  const { state, reason } = useRightCheck(action, target)
  if (state === 'hidden') return null
  if (state === 'enabled') return children(false)
  return (
    <Tooltip title={reason}>
      <span data-testid={`right-gate-${action}`}>{children(true)}</span>
    </Tooltip>
  )
}

export default RightGate
